import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import io from 'socket.io-client';
import './App.css';

// --- PERSISTENCE & CONNECTION SETUP ---
const getSessionId = () => {
  let id = localStorage.getItem('pict_sessionId');
  if (!id) {
    id = 'user_' + Math.random().toString(36).substr(2, 9) + Date.now();
    localStorage.setItem('pict_sessionId', id);
  }
  return id;
};

const SOCKET_URL = 'https://games.tolmanhome.us';
const SOCKET_PATH = '/pictionary-api/socket.io';
const sessionId = getSessionId();

// Enhanced Socket Configuration for Stability
const socket = io(SOCKET_URL, {
  path: SOCKET_PATH,
  auth: { sessionId },
  autoConnect: false,
  reconnection: true,
  reconnectionAttempts: 20,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 20000,
  transports: ['websocket', 'polling']
});

// --- AUDIO HELPERS ---
const audioCache = {};
const playSound = (type) => {
  const soundPaths = {
    tick: '/pictionary/sounds/tick.wav',
    correct: '/pictionary/sounds/correct.wav',
    join: '/pictionary/sounds/join.wav',
    win: '/pictionary/sounds/win.wav',
    lose: '/pictionary/sounds/lose.wav',
    yourTurn: '/pictionary/sounds/yourturn.wav'
  };
  
  if (!audioCache[type] && soundPaths[type]) {
    audioCache[type] = new Audio(soundPaths[type]);
  }
  
  if (audioCache[type]) {
    audioCache[type].currentTime = 0;
    audioCache[type].volume = 0.5;
    audioCache[type].play().catch(e => console.log("Audio play failed"));
  }
};

// --- DRAWING CANVAS COMPONENT ---
const DrawingCanvas = forwardRef(({ isDrawer, onDraw, onUndo, brushColor, brushSize, isEraser }, ref) => {
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPoint = useRef(null);
  const canvasSizeRef = useRef({ width: 300, height: 400 });
  const strokeHistoryRef = useRef([]); 
  const currentStrokeRef = useRef([]); 
  
  const getCanvasSize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return { width: 300, height: 400 };
    const wrapper = canvas.parentElement;
    if (!wrapper) return { width: 300, height: 400 };
    const wrapperWidth = wrapper.offsetWidth || 300;
    const aspectRatio = 3 / 4;
    return { width: wrapperWidth, height: wrapperWidth / aspectRatio };
  };
  
  const drawSegment = useCallback((data, ctx, width, height) => {
    if (!ctx) return;
    const brushWidth = data.size * width;
    
    if (data.x1 === data.x2 && data.y1 === data.y2) {
      ctx.beginPath();
      ctx.fillStyle = data.color;
      ctx.arc(data.x1 * width, data.y1 * height, brushWidth / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.beginPath();
      ctx.strokeStyle = data.color;
      ctx.lineWidth = brushWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.moveTo(data.x1 * width, data.y1 * height);
      ctx.lineTo(data.x2 * width, data.y2 * height);
      ctx.stroke();
    }
  }, []);
  
  const redrawAll = useCallback(() => {
    const ctx = contextRef.current;
    if (!ctx) return;
    const { width, height } = canvasSizeRef.current;
    
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    
    strokeHistoryRef.current.forEach(strokeGroup => {
      strokeGroup.forEach(segment => {
        drawSegment(segment, ctx, width, height);
      });
    });
  }, [drawSegment]);
  
  const drawStroke = useCallback((data) => {
    const ctx = contextRef.current;
    if (!ctx) return;
    const { width, height } = canvasSizeRef.current;
    
    if (data.type === 'clear') {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      strokeHistoryRef.current = [];
    } else if (data.type === 'undo') {
      if (strokeHistoryRef.current.length > 0) {
        strokeHistoryRef.current.pop();
        redrawAll();
      }
    } else if (data.type === 'line') {
      drawSegment(data, ctx, width, height);
      if (data.strokeId !== undefined) {
        let group = strokeHistoryRef.current.find(g => g.strokeId === data.strokeId);
        if (!group) {
          group = [];
          group.strokeId = data.strokeId;
          strokeHistoryRef.current.push(group);
        }
        group.push(data);
      }
    }
  }, [drawSegment, redrawAll]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const { width, height } = getCanvasSize();
    canvasSizeRef.current = { width, height };
    
    canvas.width = width * 2;
    canvas.height = height * 2;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    
    const ctx = canvas.getContext('2d');
    ctx.scale(2, 2);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    contextRef.current = ctx;
    
    strokeHistoryRef.current = [];
    
    const handleResize = () => {
      const { width: newWidth, height: newHeight } = getCanvasSize();
      canvasSizeRef.current = { width: newWidth, height: newHeight };
      
      canvas.width = newWidth * 2;
      canvas.height = newHeight * 2;
      canvas.style.width = `${newWidth}px`;
      canvas.style.height = `${newHeight}px`;
      
      const ctx = canvas.getContext('2d');
      ctx.scale(2, 2);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      contextRef.current = ctx;
      
      redrawAll();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [redrawAll]);
  
  useEffect(() => {
    const handleDrawUpdate = ({ drawData }) => { drawStroke(drawData); };
    const handleCanvasCleared = () => { drawStroke({ type: 'clear' }); };
    const handleUndo = () => { drawStroke({ type: 'undo' }); };
    const handleNewWord = () => { drawStroke({ type: 'clear' }); };
    const handleNextWord = () => { drawStroke({ type: 'clear' }); };
    const handleWordSkipped = () => { drawStroke({ type: 'clear' }); };
    
    socket.on('drawUpdate', handleDrawUpdate);
    socket.on('canvasCleared', handleCanvasCleared);
    socket.on('undoStroke', handleUndo);
    socket.on('newWord', handleNewWord);
    socket.on('nextWord', handleNextWord);
    socket.on('wordSkipped', handleWordSkipped);
    
    return () => {
      socket.off('drawUpdate', handleDrawUpdate);
      socket.off('canvasCleared', handleCanvasCleared);
      socket.off('undoStroke', handleUndo);
      socket.off('newWord', handleNewWord);
      socket.off('nextWord', handleNextWord);
      socket.off('wordSkipped', handleWordSkipped);
    };
  }, [drawStroke]);
  
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    return {
      x: (clientX - rect.left) / rect.width,
      y: (clientY - rect.top) / rect.height
    };
  };
  
  const currentStrokeIdRef = useRef(0);
  
  const startDrawing = (e) => {
    if (!isDrawer) return;
    e.preventDefault();
    isDrawing.current = true;
    const coords = getCoordinates(e);
    lastPoint.current = coords;
    
    currentStrokeIdRef.current = Date.now();
    currentStrokeRef.current = [];
    
    const ctx = contextRef.current;
    const { width, height } = canvasSizeRef.current;
    const color = isEraser ? '#FFFFFF' : brushColor;
    const size = isEraser ? brushSize * 2 : brushSize;
    
    ctx.beginPath();
    ctx.fillStyle = color;
    ctx.arc(coords.x * width, coords.y * height, size / 2, 0, Math.PI * 2);
    ctx.fill();
    
    const strokeData = {
      type: 'line',
      x1: coords.x,
      y1: coords.y,
      x2: coords.x,
      y2: coords.y,
      color,
      size: size / width,
      strokeId: currentStrokeIdRef.current
    };
    currentStrokeRef.current.push(strokeData);
    onDraw(strokeData);
  };
  
  const draw = (e) => {
    if (!isDrawer || !isDrawing.current || !lastPoint.current) return;
    e.preventDefault();
    
    const coords = getCoordinates(e);
    const ctx = contextRef.current;
    const { width, height } = canvasSizeRef.current;
    
    const color = isEraser ? '#FFFFFF' : brushColor;
    const size = isEraser ? brushSize * 2 : brushSize;
    
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.moveTo(lastPoint.current.x * width, lastPoint.current.y * height);
    ctx.lineTo(coords.x * width, coords.y * height);
    ctx.stroke();
    
    const strokeData = {
      type: 'line',
      x1: lastPoint.current.x,
      y1: lastPoint.current.y,
      x2: coords.x,
      y2: coords.y,
      color,
      size: size / width,
      strokeId: currentStrokeIdRef.current
    };
    currentStrokeRef.current.push(strokeData);
    onDraw(strokeData);
    
    lastPoint.current = coords;
  };
  
  const stopDrawing = () => {
    if (isDrawing.current && currentStrokeRef.current.length > 0) {
      const strokeGroup = [...currentStrokeRef.current];
      strokeGroup.strokeId = currentStrokeIdRef.current;
      strokeHistoryRef.current.push(strokeGroup);
      currentStrokeRef.current = [];
    }
    isDrawing.current = false;
    lastPoint.current = null;
  };
  
  const handleUndo = useCallback(() => {
    if (strokeHistoryRef.current.length > 0) {
      strokeHistoryRef.current.pop();
      redrawAll();
      onUndo();
    }
  }, [redrawAll, onUndo]);
  
  useImperativeHandle(ref, () => ({ handleUndo }), [handleUndo]);
  
  return (
    <canvas
      ref={canvasRef}
      className={`drawing-canvas ${isDrawer ? 'drawer' : 'viewer'}`}
      onMouseDown={startDrawing}
      onMouseMove={draw}
      onMouseUp={stopDrawing}
      onMouseLeave={stopDrawing}
      onTouchStart={startDrawing}
      onTouchMove={draw}
      onTouchEnd={stopDrawing}
    />
  );
});

// --- MAIN APP COMPONENT ---
function App() {
  // START IN INITIALIZING STATE
  const [gameState, setGameState] = useState('initializing');
  const [isConnected, setIsConnected] = useState(false);
  
  const [playerName, setPlayerName] = useState(localStorage.getItem('pict_playerName') || '');
  const [roomCode, setRoomCode] = useState('');
  const [playerId, setPlayerId] = useState(sessionId);
  const [isHost, setIsHost] = useState(false);
  const [error, setError] = useState('');
  const [availableRooms, setAvailableRooms] = useState([]);
  
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([
    { name: 'Team 1', color: '#FF6B6B', score: 0, memberIds: [] },
    { name: 'Team 2', color: '#4ECDC4', score: 0, memberIds: [] }
  ]);
  const [localTeamNames, setLocalTeamNames] = useState(['Team 1', 'Team 2']);
  const teamNameDebounceRef = useRef([null, null]);
  
  const [difficulty, setDifficulty] = useState('all');
  const [currentTeam, setCurrentTeam] = useState(0);
  const [currentDrawer, setCurrentDrawer] = useState(null);
  const [currentWord, setCurrentWord] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(60);
  
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(4);
  const [isEraser, setIsEraser] = useState(false);
  const canvasRef = useRef(null);
  
  const [guess, setGuess] = useState('');
  const [correctMessage, setCorrectMessage] = useState(null);
  const [wrongGuesses, setWrongGuesses] = useState([]);
  
  const [stealPhase, setStealPhase] = useState(false);
  const [stealGuesser, setStealGuesser] = useState(null);
  const [stealGuesserName, setStealGuesserName] = useState('');
  const [stealResult, setStealResult] = useState(null);
  
  const [roundEndData, setRoundEndData] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [winningTeam, setWinningTeam] = useState(null);
  const [confirmingCorrect, setConfirmingCorrect] = useState(false);
  
  const myPlayer = players.find(p => p.id === playerId);
  const myTeam = myPlayer?.team;
  const isDrawer = currentDrawer === playerId;
  const isGuesser = myTeam === currentTeam && !isDrawer && gameState === 'drawing';
  const isSpectator = myTeam !== currentTeam && gameState === 'drawing';
  const canSteal = stealPhase && stealGuesser === playerId;
  
  // --- CONNECTION MANAGEMENT ---
  useEffect(() => {
    if (!socket.connected) socket.connect();
    
    // Heartbeat loop
    const heartbeatInterval = setInterval(() => {
      if (socket.connected && roomCode && gameState !== 'home' && gameState !== 'initializing') {
        socket.emit('heartbeat', { roomCode });
      }
    }, 15000);

    const handleConnect = () => {
      setIsConnected(true);
      
      // Auto-rejoin logic
      const lastRoom = localStorage.getItem('pict_lastRoom');
      if (lastRoom && (roomCode === '' || gameState === 'initializing')) {
        socket.emit('rejoinGame', { roomCode: lastRoom });
      } else if (roomCode) {
        socket.emit('rejoinGame', { roomCode });
      }
    };

    const handleDisconnect = () => setIsConnected(false);

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    
    // INITIAL LOAD CHECK
    const lastRoom = localStorage.getItem('pict_lastRoom');
    if (lastRoom) {
      if (socket.connected) {
        socket.emit('rejoinGame', { roomCode: lastRoom });
      }
      // If not connected yet, handleConnect will fire soon
    } else {
      // No room to rejoin, go to home
      setGameState('home');
    }
    
    // SAFETY TIMEOUT: If still initializing after 3s (e.g. server down), go to home
    const initTimeout = setTimeout(() => {
      setGameState(current => current === 'initializing' ? 'home' : current);
    }, 3000);

    return () => {
      clearInterval(heartbeatInterval);
      clearTimeout(initTimeout);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, [roomCode, gameState]);

  // --- SOCKET GAME EVENTS ---
  useEffect(() => {
    socket.on('roomList', (rooms) => setAvailableRooms(rooms));
    
    socket.on('roomCreated', ({ roomCode, playerId, isHost }) => {
      setRoomCode(roomCode);
      setPlayerId(playerId);
      setIsHost(isHost);
      setGameState('lobby');
      localStorage.setItem('pict_lastRoom', roomCode);
    });
    
    socket.on('roomJoined', ({ roomCode, playerId, isHost, fromRejoin }) => {
      setRoomCode(roomCode);
      setPlayerId(playerId);
      setIsHost(isHost);
      // If fromRejoin, the gameSync event will handle the specific state (lobby/drawing etc)
      if (!fromRejoin) {
        setGameState('lobby');
      }
      localStorage.setItem('pict_lastRoom', roomCode);
    });
    
    socket.on('playersUpdated', ({ players, teams }) => {
      setPlayers(players);
      setTeams(teams);
    });
    
    socket.on('playerJoined', () => playSound('join'));
    socket.on('difficultyChanged', ({ difficulty }) => setDifficulty(difficulty));
    socket.on('promotedToHost', () => setIsHost(true));
    
    socket.on('kicked', () => {
      alert("You were kicked from the room.");
      localStorage.removeItem('pict_lastRoom');
      resetToHome();
    });
    
    socket.on('error', ({ message }) => {
      if (message.includes('expired') || message.includes('not found')) {
        localStorage.removeItem('pict_lastRoom');
        resetToHome();
      }
      setError(message);
      setTimeout(() => setError(''), 3000);
    });
    
    socket.on('gameStarted', ({ teams }) => {
      setTeams(teams);
      setGameState('waiting-for-drawer');
    });
    
    socket.on('gameSync', (data) => {
      setGameState(data.gameState);
      setTeams(data.teams);
      setPlayers(data.players);
      setCurrentTeam(data.currentTeam);
      setCurrentDrawer(data.currentDrawer);
      setCurrentWord(data.currentWord || '');
      setTimeRemaining(data.timeRemaining);
      setDifficulty(data.difficulty);
      setStealPhase(data.stealPhase);
      setStealGuesser(data.stealGuesser);
      if (data.winningTeam !== undefined) {
        setWinningTeam(data.winningTeam);
        setGameOver(true);
      }
    });
    
    socket.on('roundSetup', ({ currentTeam, currentDrawer, teams }) => {
      setCurrentTeam(currentTeam);
      setCurrentDrawer(currentDrawer);
      setTeams(teams);
      setGameState('waiting-for-drawer');
      setCorrectMessage(null);
      setWrongGuesses([]);
      setStealPhase(false);
      setStealResult(null);
    });
    
    socket.on('yourTurnToDraw', ({ word }) => {
      setCurrentWord(word);
      playSound('yourTurn');
      if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 100]);
    });
    
    socket.on('drawingStarted', ({ timeRemaining, currentTeam, currentDrawer }) => {
      setTimeRemaining(timeRemaining);
      setCurrentTeam(currentTeam);
      setCurrentDrawer(currentDrawer);
      setGameState('drawing');
    });
    
    socket.on('timerUpdate', ({ timeRemaining }) => {
      setTimeRemaining(timeRemaining);
      if (timeRemaining <= 10 && timeRemaining > 0) playSound('tick');
    });
    
    socket.on('correctGuess', ({ guesserName, correctWord, teams, manualConfirm }) => {
      playSound('correct');
      setTeams(teams);
      setCorrectMessage({ guesserName, correctWord, manualConfirm });
      setTimeout(() => setCorrectMessage(null), 3000);
    });
    
    socket.on('wrongGuess', ({ guesserName, guess }) => {
      setWrongGuesses(prev => [...prev.slice(-4), { guesserName, guess }]);
    });
    
    const handleAppNewWord = ({ word }) => {
      setCurrentWord(word);
      setConfirmingCorrect(false);
      setWrongGuesses([]);
    };
    
    const handleAppNextWord = ({ timeRemaining }) => {
      setTimeRemaining(timeRemaining);
      setConfirmingCorrect(false);
      setWrongGuesses([]);
    };
    
    const handleAppWordSkipped = () => {};
    
    socket.on('newWord', handleAppNewWord);
    socket.on('nextWord', handleAppNextWord);
    socket.on('wordSkipped', handleAppWordSkipped);
    socket.on('confirmCorrect', () => setConfirmingCorrect(true));
    socket.on('correctCancelled', () => setConfirmingCorrect(false));
    
    socket.on('stealPhaseStarted', ({ stealGuesser, guesserName, timeRemaining }) => {
      setStealPhase(true);
      setStealGuesser(stealGuesser);
      setStealGuesserName(guesserName);
      setTimeRemaining(timeRemaining);
      setGameState('steal-phase');
      if (stealGuesser === sessionId) {
        playSound('yourTurn');
        if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
      }
    });
    
    socket.on('stealResult', ({ stolen, guess, correctWord, guesserName, teams, passed }) => {
      setStealResult({ stolen, guess, correctWord, guesserName, passed });
      setTeams(teams);
      if (stolen) playSound('correct');
    });
    
    socket.on('roundEnded', ({ teams, wordsGuessed, currentTeam }) => {
      setTeams(teams);
      setRoundEndData({ wordsGuessed, currentTeam });
      setGameState('round-end');
      setStealPhase(false);
      setStealResult(null);
    });
    
    socket.on('gameOver', ({ teams, winningTeam, isTie }) => {
      setTeams(teams);
      setWinningTeam(winningTeam);
      setGameOver(true);
      setGameState('game-over');
      const myTeamIndex = players.find(p => p.id === playerId)?.team;
      if (!isTie) {
        if (myTeamIndex === winningTeam) playSound('win');
        else playSound('lose');
      }
    });
    
    socket.on('gameReset', ({ players, teams }) => {
      setPlayers(players);
      setTeams(teams);
      setGameState('lobby');
      setGameOver(false);
      setWinningTeam(null);
      setRoundEndData(null);
      setCurrentWord('');
      setCurrentDrawer(null);
    });
    
    socket.on('scoreAdjusted', ({ teams }) => setTeams(teams));
    
    return () => {
      socket.off('roomList'); socket.off('roomCreated'); socket.off('roomJoined');
      socket.off('playersUpdated'); socket.off('playerJoined'); socket.off('difficultyChanged');
      socket.off('promotedToHost'); socket.off('kicked'); socket.off('error');
      socket.off('gameStarted'); socket.off('gameSync'); socket.off('roundSetup');
      socket.off('yourTurnToDraw'); socket.off('drawingStarted'); socket.off('timerUpdate');
      socket.off('correctGuess'); socket.off('wrongGuess');
      socket.off('newWord', handleAppNewWord); socket.off('nextWord', handleAppNextWord);
      socket.off('wordSkipped', handleAppWordSkipped);
      socket.off('confirmCorrect'); socket.off('correctCancelled');
      socket.off('stealPhaseStarted'); socket.off('stealResult');
      socket.off('roundEnded'); socket.off('gameOver');
      socket.off('gameReset'); socket.off('scoreAdjusted');
    };
  }, [playerId, players]);
  
  useEffect(() => {
    const hasActiveDebounce = teamNameDebounceRef.current.some(t => t !== null);
    if (!hasActiveDebounce) setLocalTeamNames([teams[0].name, teams[1].name]);
  }, [teams]);
  
  const resetToHome = () => {
    setGameState('home');
    setRoomCode('');
    setIsHost(false);
    setPlayers([]);
    setTeams([
      { name: 'Team 1', color: '#FF6B6B', score: 0, memberIds: [] },
      { name: 'Team 2', color: '#4ECDC4', score: 0, memberIds: [] }
    ]);
    setLocalTeamNames(['Team 1', 'Team 2']);
  };
  
  const createRoom = () => {
    if (!playerName.trim()) { setError('Please enter your name'); return; }
    localStorage.setItem('pict_playerName', playerName.trim());
    socket.emit('createRoom', playerName.trim());
  };
  
  const joinRoom = (code) => {
    if (!playerName.trim()) { setError('Please enter your name'); return; }
    localStorage.setItem('pict_playerName', playerName.trim());
    socket.emit('joinRoom', { roomCode: code, playerName: playerName.trim() });
  };
  
  const assignTeam = (targetPlayerId, teamIndex) => socket.emit('assignTeam', { roomCode, playerId: targetPlayerId, teamIndex });
  const randomizeTeams = () => socket.emit('randomizeTeams', { roomCode });
  
  const updateTeamName = (teamIndex, name) => {
    setLocalTeamNames(prev => {
      const updated = [...prev];
      updated[teamIndex] = name;
      return updated;
    });
    if (teamNameDebounceRef.current[teamIndex]) clearTimeout(teamNameDebounceRef.current[teamIndex]);
    teamNameDebounceRef.current[teamIndex] = setTimeout(() => {
      socket.emit('updateTeamName', { roomCode, teamIndex, name });
    }, 300);
  };
  
  const changeDifficulty = (diff) => socket.emit('setDifficulty', { roomCode, difficulty: diff });
  const startGame = () => socket.emit('startGame', { roomCode });
  const drawerReady = () => socket.emit('drawerReady', { roomCode });
  const handleDraw = useCallback((drawData) => socket.emit('draw', { roomCode, drawData }), [roomCode]);
  const handleUndo = useCallback(() => socket.emit('undoStroke', { roomCode }), [roomCode]);
  const clearCanvas = () => socket.emit('clearCanvas', { roomCode });
  const submitGuess = (e) => {
    e.preventDefault();
    if (!guess.trim()) return;
    socket.emit('submitGuess', { roomCode, guess: guess.trim() });
    setGuess('');
  };
  const confirmCorrect = () => socket.emit('drawerCorrect', { roomCode });
  const skipWord = () => socket.emit('skipWord', { roomCode });
  const nextRound = () => socket.emit('nextRound', { roomCode });
  const adjustScore = (teamIndex, delta) => socket.emit('adjustScore', { roomCode, teamIndex, delta });
  const newGame = () => socket.emit('newGame', { roomCode });
  const kickPlayer = (targetId) => socket.emit('kickPlayer', { roomCode, targetId });
  const passSteal = () => socket.emit('passSteal', { roomCode });
  const leaveGame = () => {
    socket.emit('leaveRoom', { roomCode });
    localStorage.removeItem('pict_lastRoom');
    resetToHome();
  };
  
  const brushColors = [
    '#000000', '#795548', '#E53935', '#FB8C00', '#FDD835', 
    '#43A047', '#1E88E5', '#8E24AA', '#F48FB1', '#757575'
  ];
  const brushSizes = [2, 4, 8];
  
  // RENDER SECTIONS
  
  const ConnectionBanner = () => (
    !isConnected && gameState !== 'home' && gameState !== 'initializing' ? (
      <div className="connection-banner">
        ⚠️ Reconnecting...
      </div>
    ) : null
  );

  // --- INITIALIZING SCREEN (New) ---
  if (gameState === 'initializing') {
    return (
      <div className="App">
        <div className="container home-container">
          <div className="logo-section">
            <h1 className="logo">🎨 Pictionary</h1>
            <p className="tagline">Loading...</p>
          </div>
        </div>
		<RulesModal />
      </div>
    );
  }

  if (gameState === 'home') {
    return (
      <div className="App">
        <div className="container home-container">
          <div className="logo-section">
            <h1 className="logo">🎨 Pictionary</h1>
            <p className="tagline">Draw, Guess, Win!</p>
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="name-section">
            <input
              type="text"
              placeholder="Enter your name..."
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              className="input name-input"
              maxLength={15}
            />
          </div>
          <div className="action-buttons">
            <button onClick={createRoom} className="btn btn-primary">Create Game</button>
          </div>
          {availableRooms.length > 0 ? (
            <div className="rooms-section">
              <h3>Join a Game</h3>
              <div className="rooms-list">
                {availableRooms.map(room => (
                  <div key={room.code} className="room-card" onClick={() => joinRoom(room.code)}>
                    <span className="room-host">{room.hostName}'s Game</span>
                    <span className="room-players">{room.playerCount} players</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="no-games-message">No games available. Create one!</p>
          )}
        </div>
		<RulesModal />
      </div>
    );
  }
  
  if (gameState === 'lobby') {
    const unassignedPlayers = players.filter(p => p.team === null);
    const team0Players = players.filter(p => p.team === 0);
    const team1Players = players.filter(p => p.team === 1);
    const canStart = team0Players.length >= 2 && team1Players.length >= 2;
    
    return (
      <div className="App">
        <ConnectionBanner />
        <div className="container lobby-container">
          <div className="lobby-header">
            <h2>Game Lobby</h2>
            <div className="difficulty-display">
              Difficulty: <span className="difficulty-badge">{difficulty === 'all' ? 'Mixed' : difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}</span>
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          {isHost && (
            <div className="host-controls">
              <div className="difficulty-select">
                <label>Change Difficulty:</label>
                <select value={difficulty} onChange={(e) => changeDifficulty(e.target.value)}>
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                  <option value="all">All (Mixed)</option>
                </select>
              </div>
              <button onClick={randomizeTeams} className="btn btn-secondary">🎲 Randomize Teams</button>
            </div>
          )}
          <div className="teams-setup">
            <div className="unassigned-section">
              <h3>Unassigned Players</h3>
              <div className="player-list">
                {unassignedPlayers.map(player => (
                  <div key={player.id} className={`player-item ${player.isOffline ? 'offline' : ''}`}>
                    <span className="player-name">
                      {player.name} {player.id === playerId && '(You)'} 
                      {player.id === players.find(p => p.id === roomCode.host)?.id && '👑'}
                    </span>
                    {isHost && player.id !== playerId && (
                      <div className="player-actions">
                        <button className="team-btn team-0" style={{ backgroundColor: teams[0].color }} onClick={() => assignTeam(player.id, 0)}>→</button>
                        <button className="team-btn team-1" style={{ backgroundColor: teams[1].color }} onClick={() => assignTeam(player.id, 1)}>→</button>
                        <button className="kick-btn" onClick={() => kickPlayer(player.id)}>✕</button>
                      </div>
                    )}
                    {isHost && player.id === playerId && (
                      <div className="player-actions">
                        <button className="team-btn team-0" style={{ backgroundColor: teams[0].color }} onClick={() => assignTeam(player.id, 0)}>→</button>
                        <button className="team-btn team-1" style={{ backgroundColor: teams[1].color }} onClick={() => assignTeam(player.id, 1)}>→</button>
                      </div>
                    )}
                  </div>
                ))}
                {unassignedPlayers.length === 0 && <p className="empty-message">All players assigned!</p>}
              </div>
            </div>
            <div className="teams-row">
              {[0, 1].map(teamIndex => (
                <div key={teamIndex} className="team-card" style={{ borderColor: teams[teamIndex].color }}>
                  <div className="team-header" style={{ backgroundColor: teams[teamIndex].color }}>
                    {isHost ? (
                      <input type="text" value={localTeamNames[teamIndex]} onChange={(e) => updateTeamName(teamIndex, e.target.value)} className="team-name-input" maxLength={20} />
                    ) : (
                      <h3>{teams[teamIndex].name}</h3>
                    )}
                  </div>
                  <div className="team-members">
                    {players.filter(p => p.team === teamIndex).map(player => (
                      <div key={player.id} className={`team-member ${player.isOffline ? 'offline' : ''}`}>
                        <span>{player.name} {player.id === playerId && '(You)'}</span>
                        {isHost && <button className="remove-btn" onClick={() => assignTeam(player.id, null)}>✕</button>}
                      </div>
                    ))}
                    {players.filter(p => p.team === teamIndex).length < 2 && <p className="need-players">Need {2 - players.filter(p => p.team === teamIndex).length} more</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {isHost ? (
            <button onClick={startGame} className="btn btn-primary start-btn" disabled={!canStart}>{canStart ? 'Start Game' : 'Need 2+ players per team'}</button>
          ) : (
            <p className="waiting-text">Waiting for host to start...</p>
          )}
        </div>
        <LeaveButton onClick={leaveGame} />
		<RulesModal />
      </div>
    );
  }
  
  if (gameState === 'waiting-for-drawer') {
    const drawerPlayer = players.find(p => p.id === currentDrawer);
    return (
      <div className="App">
        <ConnectionBanner />
        <div className="container game-container">
          <ScoreBar teams={teams} currentTeam={currentTeam} />
          <div className="waiting-drawer">
            {isDrawer ? (
              <>
                <h2>Your turn to draw!</h2>
                <div className="word-reveal">
                  <p>Your word is:</p>
                  <h1 className="the-word">{currentWord}</h1>
                </div>
                <button onClick={drawerReady} className="btn btn-primary btn-large">Ready to Draw!</button>
              </>
            ) : (
              <>
                <h2>Get Ready!</h2>
                <p className="drawer-info">
                  <span style={{ color: teams[currentTeam].color }}>{drawerPlayer?.name}</span> is about to draw...
                </p>
                <div className="waiting-animation">🎨</div>
              </>
            )}
          </div>
        </div>
        <LeaveButton onClick={leaveGame} />
		<RulesModal />
      </div>
    );
  }
  
  if (gameState === 'drawing' || gameState === 'steal-phase') {
    const drawerPlayer = players.find(p => p.id === currentDrawer);
    const timerClass = timeRemaining <= 10 ? 'timer-warning' : '';
    return (
      <div className="App">
        <ConnectionBanner />
        <div className="container game-container drawing-screen">
          <ScoreBar teams={teams} currentTeam={currentTeam} compact />
          <div className="drawing-header">
            <div className={`timer ${timerClass}`}>⏱️ {timeRemaining}s</div>
            {isDrawer && <div className="current-word">Draw: <strong>{currentWord}</strong></div>}
            {!isDrawer && !stealPhase && <div className="drawer-info-small">{drawerPlayer?.name} is drawing</div>}
            {stealPhase && <div className="steal-info">🎯 {stealGuesserName} can steal!</div>}
          </div>
          {correctMessage && (
            <div className="correct-overlay">
              <div className="correct-message">✓ Correct! "{correctMessage.correctWord}"{correctMessage.manualConfirm ? '' : ` - ${correctMessage.guesserName}`}</div>
            </div>
          )}
          {stealResult && (
            <div className={`steal-overlay ${stealResult.stolen ? 'stolen' : 'missed'}`}>
              <div className="steal-message">
                {stealResult.stolen ? <>🎯 STOLEN! "{stealResult.correctWord}"</> : stealResult.passed ? <>⏭️ Passed! The word was "{stealResult.correctWord}"</> : <>❌ Missed! The word was "{stealResult.correctWord}"</>}
              </div>
            </div>
          )}
          <div className="canvas-section">
            <div className="canvas-wrapper">
              <DrawingCanvas ref={canvasRef} isDrawer={isDrawer && !stealPhase} onDraw={handleDraw} onUndo={handleUndo} brushColor={brushColor} brushSize={brushSize} isEraser={isEraser} />
            </div>
          </div>
          {isDrawer && !stealPhase && (
            <div className="drawer-controls">
              <div className="brush-tools">
                <div className="color-picker">
                  {brushColors.map(color => (
                    <button key={color} className={`color-btn ${brushColor === color && !isEraser ? 'active' : ''}`} style={{ backgroundColor: color }} onClick={() => { setBrushColor(color); setIsEraser(false); }} />
                  ))}
                  <button className={`eraser-btn ${isEraser ? 'active' : ''}`} onClick={() => setIsEraser(!isEraser)}>🧹</button>
                </div>
                <div className="size-picker">
                  {brushSizes.map(size => (
                    <button key={size} className={`size-btn ${brushSize === size ? 'active' : ''}`} onClick={() => setBrushSize(size)}>
                      <span style={{ width: size * 2, height: size * 2 }} className="size-dot" />
                    </button>
                  ))}
                </div>
                <button onClick={() => canvasRef.current?.handleUndo?.()} className="undo-btn">↩️ Undo</button>
                <button onClick={clearCanvas} className="clear-btn">🗑️ Clear</button>
              </div>
              <div className="drawer-actions">
                <button onClick={confirmCorrect} className={`btn btn-success ${confirmingCorrect ? 'confirming' : ''}`}>{confirmingCorrect ? '✓ Tap again to confirm' : '✓ Correct!'}</button>
                <button onClick={skipWord} className="btn btn-warning">⏭️ Skip</button>
              </div>
            </div>
          )}
          {(isGuesser || canSteal) && !correctMessage && !stealResult && (
            <form onSubmit={submitGuess} className="guess-form">
              <input type="text" value={guess} onChange={(e) => setGuess(e.target.value)} placeholder={canSteal ? "One guess to steal..." : "Type your guess..."} className="input guess-input" autoFocus />
              <button type="submit" className="btn btn-primary">Guess</button>
              {canSteal && <button type="button" onClick={passSteal} className="btn btn-secondary">Pass</button>}
            </form>
          )}
          {wrongGuesses.length > 0 && (
            <div className="wrong-guesses">
              {wrongGuesses.map((wg, i) => <span key={i} className="wrong-guess">{wg.guesserName}: "{wg.guess}"</span>)}
            </div>
          )}
          {isSpectator && !stealPhase && (
            <div className="spectator-info">
              <div className="spectator-message">👀 Watching {teams[currentTeam].name} play...</div>
              {wrongGuesses.length === 0 && !correctMessage && <div className="spectator-hint">Guesses will appear here</div>}
            </div>
          )}
        </div>
        <LeaveButton onClick={leaveGame} />
		<RulesModal />
      </div>
    );
  }
  
  if (gameState === 'round-end') {
    return (
      <div className="App">
        <ConnectionBanner />
        <div className="container round-end-container">
          <h2>Round Complete!</h2>
          <div className="round-summary">
            <div className="team-score-card" style={{ borderColor: teams[0].color }}>
              <h3 style={{ color: teams[0].color }}>{teams[0].name}</h3>
              <div className="score-display">{teams[0].score}</div>
              {isHost && <div className="score-adjust"><button onClick={() => adjustScore(0, -1)}>-</button><button onClick={() => adjustScore(0, 1)}>+</button></div>}
            </div>
            <div className="vs">VS</div>
            <div className="team-score-card" style={{ borderColor: teams[1].color }}>
              <h3 style={{ color: teams[1].color }}>{teams[1].name}</h3>
              <div className="score-display">{teams[1].score}</div>
              {isHost && <div className="score-adjust"><button onClick={() => adjustScore(1, -1)}>-</button><button onClick={() => adjustScore(1, 1)}>+</button></div>}
            </div>
          </div>
          {roundEndData && <p className="words-guessed">{teams[roundEndData.currentTeam].name} guessed {roundEndData.wordsGuessed} word{roundEndData.wordsGuessed !== 1 ? 's' : ''}!</p>}
          {isHost ? <button onClick={nextRound} className="btn btn-primary btn-large">Next Round →</button> : <p className="waiting-text">Waiting for host to continue...</p>}
        </div>
        <LeaveButton onClick={leaveGame} />
		<RulesModal />
      </div>
    );
  }
  
  if (gameState === 'game-over') {
    const myTeamIndex = myPlayer?.team;
    const isWinner = myTeamIndex === winningTeam;
    const isTie = winningTeam === -1;
    return (
      <div className={`App game-over-screen ${isWinner ? 'winner' : 'loser'}`}>
        <ConnectionBanner />
        {isWinner && !isTie && <div className="confetti-container">{[...Array(30)].map((_, i) => <div key={i} className="confetti" />)}</div>}
        {!isWinner && !isTie && <div className="tears-container">{[...Array(30)].map((_, i) => <div key={i} className="tear" />)}</div>}
        <div className="container game-over-container">
          {isTie ? <h1 className="game-over-title">It's a Tie! 🤝</h1> : <> <h1 className="game-over-title">{isWinner ? '🎉 You Win! 🎉' : '😢 Better Luck Next Time'}</h1> <h2 className="winner-team" style={{ color: teams[winningTeam].color }}>{teams[winningTeam].name} Wins!</h2> </>}
          <div className="final-scores">
            <div className="final-score-card" style={{ borderColor: teams[0].color }}><h3>{teams[0].name}</h3><div className="final-score">{teams[0].score}</div></div>
            <div className="final-score-card" style={{ borderColor: teams[1].color }}><h3>{teams[1].name}</h3><div className="final-score">{teams[1].score}</div></div>
          </div>
          {isHost ? <button onClick={newGame} className="btn btn-primary btn-large">Play Again 🎨</button> : <p className="waiting-text">Waiting for host...</p>}
        </div>
        <LeaveButton onClick={leaveGame} />
		<RulesModal />
      </div>
    );
  }
  return null;
}

const ScoreBar = ({ teams, currentTeam, compact }) => (
  <div className={`score-bar ${compact ? 'compact' : ''}`}>
    {teams.map((team, i) => (
      <div key={i} className={`score-team ${i === currentTeam ? 'active' : ''}`} style={{ borderColor: team.color }}>
        <span className="team-name" style={{ color: team.color }}>{team.name}</span>
        <span className="team-score">{team.score}</span>
      </div>
    ))}
  </div>
);

const LeaveButton = ({ onClick }) => {
  const [confirming, setConfirming] = useState(false);
  const handleClick = () => { if (confirming) onClick(); else { setConfirming(true); setTimeout(() => setConfirming(false), 3000); } };
  return (
    <div className="leave-game-container">
      <button onClick={handleClick} className={`leave-game-btn ${confirming ? 'confirming' : ''}`}>{confirming ? 'Tap again to leave' : 'Leave Game'}</button>
    </div>
  );
};

const RulesModal = () => {
  const [isOpen, setIsOpen] = React.useState(false);

  // Close on Escape key
  React.useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  return (
    <>
      <button 
        className="rules-btn" 
        onClick={() => setIsOpen(true)}
        title="How to Play"
      >
        ?
      </button>

      {isOpen && (
        <div className="rules-modal-overlay" onClick={() => setIsOpen(false)}>
          <div className="rules-modal-content" onClick={e => e.stopPropagation()}>
            <div className="rules-header">
              <h2>How to Play</h2>
              <button className="rules-close" onClick={() => setIsOpen(false)}>✕</button>
            </div>
            <div className="rules-body">
              <div className="rule-section">
                <h3>🏆 Objective</h3>
                <p>The first team to reach <span className="highlight">10 points</span> wins!</p>
              </div>

              <div className="rule-section">
                <h3>⚖️ The "Fair Play" Rule</h3>
                <p>The game will <strong>NOT</strong> end until:</p>
                <ul>
                  <li>Both teams have had the same number of turns.</li>
                  <li><strong>EVERY</strong> player has drawn at least once.</li>
                </ul>
                <p style={{fontSize: '0.9rem', marginTop: '8px'}}><em>(This means the game might continue even if a team has 10+ points, giving the other team a chance to catch up!)</em></p>
              </div>

              <div className="rule-section">
                <h3>✏️ Drawing</h3>
                <ul>
                  <li>You have <strong>60 seconds</strong> to draw the word.</li>
                  <li><strong>No letters or numbers allowed!</strong></li>
                  <li>Tap "Correct!" if your team guesses it.</li>
                  <li>You can "Skip" one word per turn.</li>
                </ul>
              </div>

              <div className="rule-section">
                <h3>🏴‍☠️ The Steal</h3>
                <p>If the timer runs out without a correct guess:</p>
                <ul>
                  <li>The opposing team gets <strong>10 seconds</strong>.</li>
                  <li>They get <strong>ONE single guess</strong> to steal the point!</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default App;